import type { AmountObject } from '@george-labs.com/design-system/types/components/Input/AmountInput';
import type { ChangeEvent } from 'react';

import { getFeatureConfig } from '@luna/feature-config';
import { isInWebView } from '@shared/george-go';

type HandlerType<T> = (value: T, amountObject?: AmountObject) => any;

export function handleLocalizedCurrencyEvent<
    T extends string | AmountObject | ChangeEvent<HTMLInputElement>,
>(originalEventHandler?: HandlerType<T>): HandlerType<T> {
    return (value, amountObject) => {
        if (!originalEventHandler) {
            return null;
        }

        if (!amountObject) {
            return originalEventHandler(value);
        }

        const { currency } = amountObject;
        const originalCurrency = currency as string;

        return originalEventHandler(value, {
            ...amountObject,
            currency: originalCurrency,
        });
    };
}

export const processValue =
    (precision?: number, disableThousandSeparatorInput?: boolean) =>
    (val: string): string => {
        const { decimalSeparator, defaultPrecision, thousandSeparator } = getFeatureConfig(
            (f) => f.base,
        );

        if (disableThousandSeparatorInput && val.includes(thousandSeparator)) {
            return val.replaceAll(thousandSeparator, '');
        }

        if (!isInWebView()) {
            return val;
        }

        if (!val.includes(decimalSeparator)) {
            return val;
        }

        const [integerPart, decimalPart] = val.split(decimalSeparator);

        if (decimalPart.length === 0) {
            return val;
        }

        return [
            integerPart,
            decimalSeparator,
            decimalPart.substring(0, precision ?? defaultPrecision),
        ].join('');
    };
